import _ from 'underscore';
import React from 'react';
import PropTypes from 'prop-types';
import { withScope, QueryParam, $current } from '@bingads-webui-react/scope-service';
import {
  Scope,
  ScopeV2,
  withSiteMap,
  siteMapPropType,
  processor,
  findDeepSiteMap,
} from '@bingads-webui-react/with-site-map';

const getCustomerUrl = (siteMap, scope, cid) => {
  const currentPage = findDeepSiteMap(siteMap, item => item.selected);
  const customerLevelScope = `?${scope.stringify(scope.getMerged({
    uid: $current,
    cid: cid || $current,
  }))}`;

  if (!_.isEmpty(currentPage)) {
    return customerLevelScope; // stay on current page, just change to customer scope
  }

  const accountSummaryItem = findDeepSiteMap(siteMap, node => node.id === 'accounts-summary');
  if (!accountSummaryItem.available) {
    return null; // account summary page is not ready, caller should handle fallback logic
  }

  // account landing page, could be directed to account summary or overview at runtime
  const accountSummaryUrl = `${accountSummaryItem.routePrefix}accounts${customerLevelScope}`;
  return accountSummaryUrl;
};

export const withCustomerUrl = (Component) => {
  const CustomerUrlWrapper = ({
    siteMap, scope, ...props
  }) =>
    (<Component {...props} customerUrl={getCustomerUrl(siteMap, scope, props.cid)} />);

  CustomerUrlWrapper.propTypes = {
    cid: PropTypes.number,
    siteMap: siteMapPropType.isRequired,
    scope: PropTypes.instanceOf(QueryParam).isRequired,
  };

  CustomerUrlWrapper.defaultProps = {
    cid: undefined,
  };

  return withSiteMap(
    withScope(React.memo(CustomerUrlWrapper)),
    [
      processor.processPath,
      processor.processPermissions,
      _.partial(processor.processScope, _, Scope.Customer),
    ]
  );
};

const getAccountUrl = (siteMap, scope, cid, aid) => {
  const mergedScope = _.defaults({
    cid: cid || scope.values.cid,
    aid: aid || scope.values.aid,
  }, scope.values);

  const currentScope = ScopeV2.getCurrentScope(mergedScope);
  if (currentScope.isChildOf(ScopeV2.Account)) {
    const trimmed = ScopeV2.trimToScope(ScopeV2.Account, mergedScope);
    return `/campaign/vnext?${scope.stringify(trimmed)}`;
  }

  const currentPage = findDeepSiteMap(siteMap, item => item.selected);
  const mergedQuery = `?${scope.stringify(mergedScope)}`;
  if (!_.isEmpty(currentPage)) {
    return mergedQuery; // stay on current page, just change to account scope
  }
  return `/campaign/vnext${mergedQuery}`; // campaign landing page with account scope
};

export const withAccountUrl = (Component) => {
  const AccountUrlWrapper = ({
    siteMap, scope, ...props
  }) =>
    (<Component {...props} accountUrl={getAccountUrl(siteMap, scope, props.cid, props.aid)} />);

  AccountUrlWrapper.propTypes = {
    cid: PropTypes.number,
    aid: PropTypes.number,
    siteMap: siteMapPropType.isRequired,
    scope: PropTypes.instanceOf(QueryParam).isRequired,
  };

  AccountUrlWrapper.defaultProps = {
    cid: undefined,
    aid: undefined,
  };

  return withSiteMap(
    withScope(React.memo(AccountUrlWrapper)),
    [
      processor.processPath,
      processor.processPermissions,
      _.partial(processor.processScope, _, Scope.Account),
    ]
  );
};
